import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPaginatedAdsApi } from "../../api/adsApi";
import { listAdCommentsApi } from "../../api/commentApi";
import type { Ad } from "../../features/ads/adsTypes";
import { getLocalizedText } from "../../features/ads/i18nFields";
import ShimmerImage from "../../components/ShimmerImage/ShimmerImage";
import { AD_IMAGE_PLACEHOLDER } from "../../utils/adImagePlaceholder";
import styles from "./OwnerAds.module.css";

const placeholder = AD_IMAGE_PLACEHOLDER;

const normalizeAdsListItem = (payload: any): Ad => {
  const raw = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const normalizedPhotos = Array.isArray(raw?.photos)
    ? raw.photos
        .map((photo: any) =>
          typeof photo === "string"
            ? photo
            : photo?.url || photo?.name || photo?.path || photo?.src
        )
        .filter(Boolean)
    : [];

  return {
    ...raw,
    id: raw?.id ?? raw?._id,
    title_i18n: raw?.title_i18n || { fr: "", en: "", ar: "" },
    content_i18n: raw?.content_i18n || { fr: "", en: "", ar: "" },
    adress_i18n: raw?.adress_i18n || { fr: raw?.adress || "", en: "", ar: "" },
    photos: normalizedPhotos,
  } as Ad;
};

const OwnerAdsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { ownerId } = useParams<{ ownerId: string }>();
  const location = useLocation();
  const locale = i18n.resolvedLanguage || "fr";
  const ownerNameFromState =
    (location.state as { ownerName?: string } | null)?.ownerName?.trim() || "";

  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ratingsByAdId, setRatingsByAdId] = useState<Record<string, { averageRating: number; count: number }>>({});

  useEffect(() => {
    if (!ownerId) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getPaginatedAdsApi({
          idOwner: ownerId,
          limit: 48,
          status: true,
        });
        if (cancelled) return;
        const list = Array.isArray(response?.data?.data)
          ? response.data.data.map((entry: any) => normalizeAdsListItem(entry))
          : [];
        setAds(list);
      } catch (err: any) {
        if (cancelled) return;
        const message =
          (typeof err?.response?.data?.message === "string" && err.response.data.message) ||
          (typeof err?.message === "string" && err.message) ||
          t("ownerAds.errorFallback");
        setError(message);
        setAds([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [ownerId, t]);

  useEffect(() => {
    if (!ads.length) {
      setRatingsByAdId({});
      return;
    }
    let cancelled = false;

    const run = async () => {
      const adIds = ads
        .map((entry) => String(entry?.id || (entry as any)?._id || "").trim())
        .filter(Boolean);
      if (!adIds.length) {
        setRatingsByAdId({});
        return;
      }

      const results = await Promise.allSettled(
        adIds.map(async (adId) => {
          const response = await listAdCommentsApi(adId);
          return {
            adId,
            averageRating: Number(response?.data?.averageRating || 0),
            count: Number(response?.data?.count || 0),
          };
        })
      );

      if (cancelled) return;
      const next: Record<string, { averageRating: number; count: number }> = {};
      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        next[result.value.adId] = {
          averageRating: result.value.averageRating,
          count: result.value.count,
        };
      });
      setRatingsByAdId(next);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [ads]);

  const resolvedOwnerName = useMemo(() => {
    if (ownerNameFromState) return ownerNameFromState;
    const first = ads[0] as any;
    const owner = first?.idOwner;
    if (owner && typeof owner === "object") {
      const label = `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim();
      if (label) return label;
    }
    return t("adDetails.owner");
  }, [ads, ownerNameFromState, t]);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>{t("ownerAds.title", { owner: resolvedOwnerName })}</h1>
          <p>{t("ownerAds.subtitle")}</p>
        </div>

        {loading && <p className={styles.state}>{t("ownerAds.loading")}</p>}
        {!loading && error && <p className={styles.state}>{t("ownerAds.error", { error })}</p>}
        {!loading && !error && ads.length === 0 && (
          <p className={styles.state}>{t("ownerAds.empty")}</p>
        )}

        {!loading && !error && ads.length > 0 && (
          <section className={styles.grid}>
            {ads.map((ad) => {
              const adId = ad.id || (ad as any)?._id;
              if (!adId) return null;

              const localizedTitle = getLocalizedText(ad.title_i18n, locale) || t("ownerAds.fallbackTitle");
              const localizedAddress = getLocalizedText(ad.adress_i18n, locale, ad.adress || "") || t("ownerAds.fallbackAddress");
              const cover = Array.isArray(ad.photos) && ad.photos[0] ? ad.photos[0] : placeholder;
              const currencyCode = String(ad.currency || "EUR").toUpperCase();
              const formattedPrice = `${new Intl.NumberFormat(locale).format(Number(ad.price || 0))} ${currencyCode}`;
              const normalizedType = String(ad.type || "").trim().toLowerCase();
              const localizedType = t(`adTypes.${normalizedType}`, { defaultValue: ad.type || t("home.segments.apartment") });
              const rating = ratingsByAdId[String(adId)] || { averageRating: 0, count: 0 };
              const rounded = Math.max(0, Math.min(5, Math.round(rating.averageRating)));
              const reviewCount = Math.max(0, Number(rating.count || 0));

              return (
                <Link key={String(adId)} to={`/ads/${adId}`} className={styles.card}>
                  <div className={styles.coverWrap}>
                    <div className={styles.cardBadges}>
                      <span className={styles.badgePrimary}>{localizedType}</span>
                      <span className={styles.badgeSecondary}>{`${ad.area || 0} m2`}</span>
                    </div>
                    <ShimmerImage src={cover} alt={localizedTitle} className={styles.image} />
                  </div>
                  <div className={styles.cardBody}>
                    <h3>{localizedTitle}</h3>
                    <p>{localizedAddress}</p>
                    <div className={styles.cardBottom}>
                      <strong>{formattedPrice}</strong>
                      <div className={styles.ratingWrap}>
                        <span className={styles.ratingInfoBubble} title={t("ownerAds.reviewCountTitle", { count: reviewCount })}>
                          <svg
                            className={styles.ratingInfoSvg}
                            viewBox="0 0 96 104"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              d="M30 70c0-12.8 14.1-23 31.5-23S93 57.2 93 70s-14.1 23-31.5 23c-4.2 0-8.1-.6-11.8-1.8-5.2 4.2-10.8 6.3-16.8 6.2 4-2.2 6.9-5.5 8.7-9.7C34 83.8 30 77.5 30 70Z"
                              fill="#ffffff"
                              stroke="#f07a24"
                              strokeWidth="3.2"
                              strokeLinejoin="round"
                            />
                            <text
                              x="61"
                              y="72"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              className={styles.ratingInfoDots}
                            >
                              {reviewCount}
                            </text>
                          </svg>
                        </span>
                        <span className={styles.ratingStars} aria-hidden="true">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={`${styles.ratingStar} ${star <= rounded ? styles.ratingStarActive : styles.ratingStarInactive}`}
                            >
                              ★
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </section>
        )}
      </div>
    </div>
  );
};

export default OwnerAdsPage;
